import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { useRole } from '@/hooks/use-role';
import { createJobQuote, jobQuotesQueryKey, listJobQuotes } from '@/lib/api/quotes';
import { formatMoney } from '@/lib/utils';

import { AddQuoteModal } from '@/routes/quotes/add-quote-modal';
import {
  quotePhase,
  quotePhaseColorClass,
  quotePhaseLabel,
} from '@/routes/quotes/helpers';
import { QuoteDetailModal } from '@/routes/quotes/quote-detail-modal';
import type { NewQuoteSubmission } from '@/routes/quotes/types';

interface Props {
  businessId: string;
  jobId: string;
  jobTitle: string;
}

// Quotes section of the job detail modal — mirrors `visit-crew-section.tsx`.
// Self-contained: owns its list query + create mutation; the shared
// `QuoteDetailModal` handles per-quote edit / send / approve / respond.
export function JobQuotesSection({ businessId, jobId, jobTitle }: Props) {
  const { t } = useLanguage();
  const { isOwner, hasPermission } = useRole();
  const canManage = isOwner || hasPermission('create_quotes');

  const [activeQuoteId, setActiveQuoteId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [autoShareOnOpen, setAutoShareOnOpen] = useState(false);

  const quotesQuery = useQuery({
    queryKey: jobQuotesQueryKey(businessId, jobId),
    queryFn: () => listJobQuotes(businessId, jobId),
  });

  const createMutation = useMutation({
    mutationFn: (s: NewQuoteSubmission) =>
      createJobQuote(businessId, jobId, {
        total_amount: s.total_amount,
        line_items: s.line_items,
        notes: s.notes,
      }),
    meta: { successMessage: t.quoteCreated },
    onSuccess: (quote) => {
      setIsAdding(false);
      setAutoShareOnOpen(true);
      setActiveQuoteId(quote.id);
    },
  });

  const quotes = quotesQuery.data ?? [];

  return (
    <div className="px-6 py-5 border-t flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t.quotesTitle}</h3>
        <Button
          type="button"
          size="xs"
          variant="outline"
          data-testid="job-add-quote"
          onClick={() => setIsAdding(true)}
        >
          <PlusIcon className="w-3 h-3" />
          {t.addQuote}
        </Button>
      </div>

      {quotesQuery.isPending ? (
        <p className="text-sm text-muted-foreground">{t.loading}</p>
      ) : quotes.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t.noQuotesFound}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {quotes.map((quote) => {
            const phase = quotePhase(quote);
            return (
              <li key={quote.id}>
                <button
                  type="button"
                  onClick={() => {
                    setAutoShareOnOpen(false);
                    setActiveQuoteId(quote.id);
                  }}
                  data-testid={`quote-row-${quote.id}`}
                  className="w-full flex items-center gap-3 text-sm rounded-md px-2 py-1.5 hover:bg-muted transition-colors"
                >
                  <span className="flex-1 text-left font-medium">
                    {formatMoney(quote.total_amount)}
                  </span>
                  {quote.line_items.length > 0 && (
                    <span className="text-[11px] text-muted-foreground">
                      {quote.line_items.length}
                    </span>
                  )}
                  <span
                    className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${quotePhaseColorClass(phase)}`}
                  >
                    {quotePhaseLabel(phase, t)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {isAdding && (
        <AddQuoteModal
          businessId={businessId}
          jobId={jobId}
          jobTitle={jobTitle}
          onClose={() => setIsAdding(false)}
          onSave={(s) => createMutation.mutate(s)}
          isSaving={createMutation.isPending}
        />
      )}

      {activeQuoteId && (
        <QuoteDetailModal
          businessId={businessId}
          jobId={jobId}
          quoteId={activeQuoteId}
          canApprove={isOwner}
          canManage={canManage}
          autoShareLink={autoShareOnOpen}
          onClose={() => {
            setActiveQuoteId(null);
            setAutoShareOnOpen(false);
          }}
        />
      )}
    </div>
  );
}
